import CheckIcon from '@mui/icons-material/Check';
import SettingsApplicationsIcon from '@mui/icons-material/SettingsApplications';
import {
  Box,
  Button,
  Card,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Typography,
} from '@mui/material';
import React, { useState } from 'react';
import AxiosConfig from '../../../../../../../config/AxiosConfig';
import ArrowReturnIcon from '../../../../../svg-icons/ArrowReturnIcon';
import LoadingButton from '../../../../../utils/common/LoadingButton';
import { getFormattedNote } from '../utils';
import PickUpInfos from './PickUpInfos.jsx';
import { setSnackBar } from '../../../../../../states/app/staticSlice.js';
import { useDispatch } from 'react-redux';
const ManageRetraction = ({
  orderDetails,
  handleShowComponent,
  profile,
  isConfirm = false,
}) => {
  const [confirmType, setConfirmType] = useState(null);
  const [formData, setFormData] = useState({
    trackingNumber: orderDetails?.retraction?.pickUps?.[0]?.trackingNumber,
    carrier: orderDetails?.retraction?.pickUps?.[0]?.carrier?.code,
  });
  const [formError, setFormError] = useState({
    trackingNumber: '',
    carrier: '',
  });
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [formattedNote, setFormattedNote] = useState('');
  const dispatch = useDispatch();

  const handleFormChange = ({ key, value }) => {
    setFormData((prevData) => ({
      ...prevData,
      [key]: value,
    }));

    // Reset error when user types
    if (formError[key]) {
      setFormError((prevErrors) => ({
        ...prevErrors,
        [key]: null,
      }));
    }
  };

  const validateForm = (submitType) => {
    const newErrors = {};
    let isValid = true;

    if (!formData.trackingNumber && submitType === 'retractUpdate') {
      newErrors.trackingNumber = 'Le numéro de tracking est requis';
      isValid = false;
    }

    if (!formData.carrier && submitType === 'retractUpdate') {
      newErrors.carrier = 'Le transporteur est requis';
      isValid = false;
    }

    setFormError(newErrors);
    return isValid;
  };

  const handleSubmit = (submitType) => {
    console.log('validateForm', validateForm(submitType));
    if (validateForm()) {
      setConfirmType(submitType);
      const formattedData = {
        confirmText:
          submitType === 'retractConfirm'
            ? `Est-ce que tous les colis sont de retour en stock? Êtes-vous sûr de vouloir confirmer la rétractation ?
               Après confirmation, le remboursement de la commande sera transmis au service finance.`
            : 'Êtes-vous sûr de vouloir enregistrer ces informations de transport ?',
        title:
          submitType === 'retractConfirm'
            ? 'Confirmation de la rétractation'
            : 'Mise à jour des informations de transport',
        note:
          submitType === 'retractConfirm'
            ? getFormattedNote(
                profile?.firstName + ' ' + profile?.lastName,
                `Date ${new Date().toLocaleDateString()}`,
                'Rétractation confirmée',
                submitType
              )
            : getFormattedNote(
                profile?.firstName + ' ' + profile?.lastName,
                `Transporteur: ${formData.carrier}, Numéro de suivi: ${
                  formData.trackingNumber
                }, Date ${new Date().toLocaleDateString()}`,
                'Informations de transport mises à jour',
                submitType
              ),
      };
      setFormattedNote(formattedData);
      setIsDialogOpen(true);
    }
  };

  const handleConfirm = async () => {
    setIsLoading(true);
    try {
      if (confirmType === 'retractConfirm') {
        // Confirmation de la rétractation
        const confirmedRetraction = await AxiosConfig.create(
          'put',
          `/orders/${orderDetails?.reference}/orderRetraction/confirm`
        )
          .call({
            note: formattedNote?.note,
            userId: profile?._id,
            ticketId: orderDetails?.ticket?._id,
            retractionId: orderDetails?.retraction?._id,
          })
          .getResponseData();

        console.log('handleConfirm confirmedRetraction', confirmedRetraction);

        if (!confirmedRetraction) {
          throw new Error('Failed to confirm retraction');
        }
      }

      if (confirmType === 'retractUpdate') {
        // mise à jour des informations de transport
        const updatedPickup = await AxiosConfig.create(
          'put',
          `/orderretractionpickups/${orderDetails?.retraction?.pickUps?.[0]?._id}`
        )
          .call({
            trackingNumber: formData.trackingNumber,
            carrier: formData.carrier,
            note: formattedNote?.note,
            userId: profile?._id,
            ticketId: orderDetails?.ticket?._id,
          })
          .getResponseData();

        if (!updatedPickup) {
          throw new Error('Failed to update pickup information');
        }
      }

      setIsDialogOpen(false);
      handleShowComponent(null);
      const messageError =
        confirmType === 'retractConfirm'
          ? 'La rétractation a été confirmée avec succès.'
          : 'Les informations de transport ont été mises à jour avec succès.';
      dispatch(
        setSnackBar({ open: true, message: messageError, color: 'success' })
      );
    } catch (error) {
      console.error('Failed to manage retraction:', error);
      const messageError = `'Une erreur est survenue lors de la gestion de la rétractation.`;
      dispatch(
        setSnackBar({ open: true, message: messageError, color: 'error' })
      );
      setIsDialogOpen(false);
    } finally {
      setIsLoading(false);
      setConfirmType(null);
    }
  };

  return (
    <>
      <Card
        elevation={5}
        sx={{
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          width: '100%',
          padding: 2,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            gap: 2,
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              gap: 1,
            }}
          >
            {isConfirm ? (
              <CheckIcon fontSize="large" color="primary" />
            ) : (
              <SettingsApplicationsIcon fontSize="large" color="primary" />
            )}
            <Typography color="primary">
              {isConfirm
                ? 'Confirmer ma rétractation'
                : 'Gérer la rétractation'}
            </Typography>
          </Box>
          <Button
            onClick={() => handleShowComponent(null)}
            variant="outlined"
            size="small"
            sx={{ position: 'absolute', top: 16, left: 16 }}
          >
            <ArrowReturnIcon fontSize="small" />
            <span style={{ marginLeft: 4, fontSize: '0.9em' }}>Retour</span>
          </Button>
        </Box>
      </Card>

      {!isConfirm && (
        <Box sx={{ mt: 3 }}>
          <Card
            elevation={5}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
              width: '100%',
              padding: 2,
            }}
          >
            <Typography variant="h6" color="primary" sx={{ mb: 2 }}>
              Informations de transport
            </Typography>

            <PickUpInfos
              formData={formData}
              handleFormChange={handleFormChange}
              formError={formError}
            />

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
              <Button
                variant="contained"
                color="primary"
                onClick={() => handleSubmit('retractUpdate')}
              >
                Enregistrer
              </Button>
            </Box>
          </Card>
        </Box>
      )}
      {isConfirm && (
        <Box sx={{ mt: 3 }}>
          <Card
            elevation={5}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
              width: '100%',
              padding: 2,
            }}
          >
            <Typography variant="h6" color="primary" sx={{ mb: 2 }}>
              Confirmer la rétractation
            </Typography>

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
              <Button
                variant="contained"
                color="primary"
                onClick={() => handleSubmit('retractConfirm')}
              >
                Confirmer
              </Button>
            </Box>
          </Card>
        </Box>
      )}
      <Dialog open={isDialogOpen} onClose={() => setIsDialogOpen(false)}>
        <DialogTitle>{formattedNote?.title}</DialogTitle>
        <DialogContent>
          <DialogContentText>{formattedNote?.confirmText}</DialogContentText>
          {formattedNote?.note && (
            <>
              <Typography sx={{ mt: 2 }}>
                <strong>Note:</strong>
              </Typography>
              <Typography
                sx={{
                  fontStyle: 'italic',
                  padding: 2,
                  border: 1,
                  backgroundColor: 'rgba(0, 0, 0, 0.02)',
                  borderColor: 'rgba(0, 0, 0, 0.3)',
                  borderRadius: 1,
                  mt: 1,
                }}
              >
                {formattedNote.note}
              </Typography>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setIsDialogOpen(false)}
            color="primary"
            variant="outlined"
          >
            Annuler
          </Button>
          <LoadingButton
            isLoading={isLoading}
            onClick={handleConfirm}
            color="primary"
            variant="contained"
          >
            Confirmer
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ManageRetraction;
